import { addonKindsOf } from '@mcpanel/core';
import { OperationBoundary } from '../components/OperationBoundary';
import { EditBoundary } from '../components/EditBoundary';
import { api3 } from '../api/client-p3';
import type { ServerView } from '../api/types';
import type { ServerMods } from '../api/types-p3';
import { errorMessage, useApp } from '../app-context';
import { ErrorAlert } from '../components/ui';
import { usePoll } from '../hooks/usePoll';
import { LauncherPolicyCard } from './LauncherPolicyCard';
import { LauncherSettingsCard } from './LauncherSettingsCard';
import { LauncherGateCard } from './LauncherGateCard';
import { PackVisibility } from './PackVisibility';

function PackSection({ serverId, data, onChange }: { serverId: string; data: ServerMods; onChange: () => void }) {
  const { canOperate, notify } = useApp();
  const setAdminOnly = async (adminOnly: boolean) => {
    try {
      await api3.setPackAdminOnly(serverId, adminOnly);
      onChange();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };
  const url = data.pack.url;
  return (
    <section className="card card-body stack" style={{ gap: 8 }}>
      <h2 data-search-id="launcher-publish">ランチャーに公開する</h2>
      <PackVisibility pack={data.pack} disabled={!canOperate} onChange={(adminOnly) => void setAdminOnly(adminOnly)} />
      <p className="small muted" style={{ margin: 0 }}>
        Prism Launcher に読み込ませると、このサーバーと同じ Mod 構成で遊べます（クライアント側に必要な Mod
        だけが入ります）。
      </p>
      {url && (
        <div className="stack" style={{ gap: 8 }}>
          <div className="row">
            <a className="btn primary small" href={url.replace(/pack\.mrpack$/, 'prism-instance.zip')}>
              Prism Launcher 用をダウンロード
            </a>
            <a className="btn small" href={url}>
              .mrpack をダウンロード
            </a>
          </div>
          <ol className="small muted" style={{ margin: 0, paddingLeft: 20 }}>
            <li>Prism Launcher で「インスタンスを追加」→「インポート」を開く</li>
            <li>ダウンロードした zip を選んで追加する</li>
            <li>起動するたびに、サーバーと同じ Mod 構成へ自動で更新される</li>
          </ol>
          <div className="secret-box">
            <code style={{ flex: 1 }}>{url}</code>
            <button
              className="btn small"
              onClick={() => void navigator.clipboard.writeText(url).then(() => notify('コピーしました'))}
            >
              コピー
            </button>
          </div>
        </div>
      )}
      {data.pack.excluded.length > 0 && (
        <div className="alert info small">
          .mrpack には Modrinth の Mod だけが入ります。次のものは Prism Launcher 用（自動更新）でだけ配られます:{' '}
          {data.pack.excluded.join('、')}
        </div>
      )}
      {data.customMods.length > 0 && (
        <div className="alert warn small">
          手動で入れた Mod（{data.customMods.map((m) => m.fileName).join('、')}
          ）は再配布の許諾が確認できないため、パックに含めません。プレイヤーに別途入れてもらう必要があります。
        </div>
      )}
    </section>
  );
}

function LauncherTabContent({ server }: { server: ServerView }) {
  const mods = usePoll(() => api3.serverMods(server.id), 30000, [server.id]);
  return (
    <div className="stack">
      <ErrorAlert error={mods.error} />
      <LauncherPolicyCard serverId={server.id} />
      <LauncherGateCard server={server} />
      {mods.data && <PackSection serverId={server.id} data={mods.data} onChange={() => void mods.reload()} />}
    </div>
  );
}
export function LauncherTab({ server }: { server: ServerView }) {
  // バニラ・プラグインのサーバーでも、プレイヤー側だけの Mod（クライアント Mod テンプレート）は配れる
  if (!addonKindsOf(server.software.family).mods)
    return (
      <div className="stack">
        <LauncherSettingsCard server={server} />
        <LauncherGateCard server={server} />
      </div>
    );
  return (
    <OperationBoundary serverId={server.id}>
      <EditBoundary resource={{ target: 'server:' + server.id, screen: 'mods' }} revision={String(server.revision)}>
        <LauncherTabContent server={server} />
      </EditBoundary>
    </OperationBoundary>
  );
}
